import React, { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { SearchContactsItem, GetContactResultResponse } from '../../api/api-client';
import { useQueryState } from 'nuqs';
interface ViewContextType {
  currentContactId: string | null;
  selectedContact: SearchContactsItem | null;
  fileData: GetContactResultResponse | null;
  viewContactId: (contactId: string | null) => void;
  selectContact: (contact: SearchContactsItem) => void;
  loadFileData: (data: GetContactResultResponse) => void;
  closeContact: () => void;
}

const ViewContext = createContext<ViewContextType | undefined>(undefined);

export const ViewProvider = ({ children }: { children: ReactNode }) => {
  const [currentContactId, setCurrentContactId] = useQueryState('contactId', {
    history: 'push'
  });
  const [selectedContact, setSelectedContact] = useState<SearchContactsItem | null>(null);
  const [fileData, setFileData] = useState<GetContactResultResponse | null>(null);

  const viewContactId = useCallback((contactId: string | null) => {
    if (!contactId) {
      setCurrentContactId(null);
      setSelectedContact(null);
      setFileData(null);
      return;
    }

    setCurrentContactId(contactId);
    setFileData(null);

    const placeholder: SearchContactsItem = {
      id: contactId,
      initiationTimestamp: new Date().toISOString(),
      disconnectTimestamp: null,
      channel: null,
      isPending: false,
      agent: null,
      initiationMethod: null
    };
    setSelectedContact(placeholder);
  }, []);

  const selectContact = useCallback((contact: SearchContactsItem) => {
    setSelectedContact(contact);
    setFileData(null);
    setCurrentContactId(contact.id);
  }, []);

  const loadFileData = useCallback((data: GetContactResultResponse) => {
    setFileData(data);
    setSelectedContact(null);
    setCurrentContactId(data.contact.Id ?? null);
  }, []);

  const closeContact = useCallback(() => {
    setSelectedContact(null);
    setFileData(null);
    setCurrentContactId(null);
  }, []);

  return (
    <ViewContext.Provider
      value={{
        currentContactId,
        selectedContact,
        fileData,
        viewContactId,
        selectContact,
        loadFileData,
        closeContact
      }}
    >
      {children}
    </ViewContext.Provider>
  );
};

export const useViewContext = () => {
  const context = useContext(ViewContext);
  if (context === undefined) {
    throw new Error('useViewContext must be used within a ViewProvider');
  }

  return context;
};
